import { gql, NetworkStatus, TypedDocumentNode } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { DateFormField, EnumSelectFormField, InputFormField } from '@repo/shared-client/components/fields'
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Progress,
  RadioGroup,
  RadioGroupItem,
  Spinner,
} from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import LogoSymbol from '@web/src/components/primatives/logo-symbol/logo-symbol'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import WelcomePageProfile from '@web/src/features/profile/welcome-page-profile/welcome-page-profile'
import { useAuth } from '@web/src/hooks/use-auth'
import { env } from '@web/src/lib/env.client'
import { PartnerInfo } from '@web/src/lib/fragments'
import i18n from '@web/src/lib/intl/i18n'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { GenderEnum } from '@web/src/lib/types/enumTypes'
import { validateDateOfBirth } from '@web/src/lib/utilsClient'
import { InfoIcon, LinkIcon, UserPlusIcon } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import type {
  FindWelcomeOnboardingQuery,
  FindWelcomeOnboardingQueryVariables,
  RelationshipEnum,
  UpdateProfileSignUpStepMutation,
  UpdateProfileSignUpStepMutationVariables,
  WelcomeCreatePartnerMutation,
  WelcomeCreatePartnerMutationVariables,
  WelcomeDeletePartnerMutation,
  WelcomeDeletePartnerMutationVariables,
  WelcomeUpdatePartnerMutation,
  WelcomeUpdatePartnerMutationVariables,
} from 'types/graphql'

const WELCOME_ONBOARDING_QUERY: TypedDocumentNode<FindWelcomeOnboardingQuery, FindWelcomeOnboardingQueryVariables> =
  gql`
    query FindWelcomeOnboardingQuery($profileId: String!) {
      profile: profile(id: $profileId) {
        id
        name
        relationship
        signUpStep
        partners {
          ...PartnerInfo
        }
      }
    }
    ${PartnerInfo}
  `

export const UPDATE_PROFILE_SIGN_UP_STEP: TypedDocumentNode<
  UpdateProfileSignUpStepMutation,
  UpdateProfileSignUpStepMutationVariables
> = gql`
  mutation UpdateProfileSignUpStep($id: String!, $input: UpdateProfileInput!) {
    updateProfile(id: $id, input: $input) {
      id
      relationship
      signUpStep
      signUpCompleted
    }
  }
`

const WELCOME_CREATE_PARTNER: TypedDocumentNode<WelcomeCreatePartnerMutation, WelcomeCreatePartnerMutationVariables> =
  gql`
    mutation WelcomeCreatePartner($input: CreatePartnerInput!) {
      createPartner(input: $input) {
        ...PartnerInfo
      }
    }
    ${PartnerInfo}
  `

const WELCOME_UPDATE_PARTNER: TypedDocumentNode<WelcomeUpdatePartnerMutation, WelcomeUpdatePartnerMutationVariables> =
  gql`
    mutation WelcomeUpdatePartner($id: String!, $input: UpdatePartnerInput!) {
      updatePartner(id: $id, input: $input) {
        ...PartnerInfo
      }
    }
    ${PartnerInfo}
  `

const WELCOME_DELETE_PARTNER: TypedDocumentNode<WelcomeDeletePartnerMutation, WelcomeDeletePartnerMutationVariables> =
  gql`
    mutation WelcomeDeletePartner($id: String!) {
      deletePartner(id: $id) {
        id
      }
    }
  `

/** Step values are persisted in `Profile.signUpStep`; keep them stable when steps are hidden. */
const WelcomeStep = {
  Relationship: 0,
  AboutYou: 1,
  Partner: 2,
  Photo: 3,
} as const

type WelcomeStep = (typeof WelcomeStep)[keyof typeof WelcomeStep]

const COUPLE_RELATIONSHIPS: readonly RelationshipEnum[] = ['COUPLE', 'ENGAGED', 'LONG_TERM', 'MARRIED']

/** Nickname given to the second partner by the previous signup flow. */
const LEGACY_PARTNER_PLACEHOLDER_NICKNAME = 'Your Partner'

const { PRIVATE: _privateGender, ...SELECTABLE_GENDERS } = GenderEnum

type WelcomeProfile = NonNullable<FindWelcomeOnboardingQuery['profile']>
type WelcomePartner = NonNullable<NonNullable<WelcomeProfile['partners']>[number]>

const partnersOf = (profile: WelcomeProfile): WelcomePartner[] =>
  (profile.partners ?? []).filter((partner): partner is WelcomePartner => !!partner)

const isCoupleRelationship = (relationship?: RelationshipEnum | null) =>
  !!relationship && COUPLE_RELATIONSHIPS.includes(relationship)

const isBlankPartner = (partner?: WelcomePartner | null) =>
  !!partner &&
  !partner.profileId &&
  partner.gender === 'PRIVATE' &&
  (!partner.nickName || partner.nickName === LEGACY_PARTNER_PLACEHOLDER_NICKNAME)

const visibleStepsFor = (relationship?: RelationshipEnum | null): WelcomeStep[] =>
  isCoupleRelationship(relationship)
    ? [WelcomeStep.Relationship, WelcomeStep.AboutYou, WelcomeStep.Partner, WelcomeStep.Photo]
    : [WelcomeStep.Relationship, WelcomeStep.AboutYou, WelcomeStep.Photo]

/** `PRIVATE` is a valid answer on About you, so progress past it is tracked by the saved step. */
const hasCompletedAboutYou = (profile: WelcomeProfile) => profile.signUpStep > WelcomeStep.AboutYou

const resumeStepFor = (profile: WelcomeProfile): WelcomeStep => {
  const [primaryPartner, secondPartner] = partnersOf(profile)
  if (!profile.relationship || profile.relationship === 'UNDEFINED') {
    return WelcomeStep.Relationship
  }
  if (!primaryPartner || (primaryPartner.gender === 'PRIVATE' && !hasCompletedAboutYou(profile))) {
    return WelcomeStep.AboutYou
  }
  if (isCoupleRelationship(profile.relationship) && (!secondPartner || isBlankPartner(secondPartner))) {
    return WelcomeStep.Partner
  }
  return WelcomeStep.Photo
}

const toDateInputValue = (value?: string | Date | null) => (value ? new Date(value).toISOString() : null)

const toDate = (value: string | Date) => (value instanceof Date ? value : new Date(value))

type StepFormProps = {
  formId: string
  onSaved: () => void
  onSubmittingChange: (submitting: boolean) => void
}

const useRelationshipOptions = () => {
  const { t } = useTranslation()

  return [
    { value: 'SINGLE', label: t('Single'), description: t("It's just me") },
    { value: 'COUPLE', label: t('Couple'), description: t("We're together as a couple") },
    { value: 'ENGAGED', label: t('Engaged'), description: t("We're engaged") },
    { value: 'LONG_TERM', label: t('Long term'), description: t("We're in a long-term relationship") },
    { value: 'MARRIED', label: t('Married'), description: t("We're married") },
    { value: 'OPEN', label: t('Open'), description: t("I'm in an open relationship") },
    { value: 'POLY', label: t('Poly'), description: t('I may have more than one partner') },
  ] satisfies { value: RelationshipEnum; label: string; description: string }[]
}

const RelationshipStep = ({
  value,
  onValueChange,
}: {
  value: RelationshipEnum | null
  onValueChange: (value: RelationshipEnum) => void
}) => {
  const { t } = useTranslation()
  const options = useRelationshipOptions()

  return (
    <RadioGroup
      aria-label={t('Relationship status')}
      className="grid-cols-1 gap-3 sm:grid-cols-2"
      onValueChange={(next) => onValueChange(next as RelationshipEnum)}
      value={value ?? ''}
    >
      {options.map((option) => (
        // biome-ignore lint/a11y/noLabelWithoutControl: RadioGroupItem renders the control inside the label
        <label
          className={cn(
            'flex cursor-pointer items-start gap-3 rounded-lg border border-border p-4 transition-colors hover:bg-accent/50',
            value === option.value && 'border-primary bg-primary/5'
          )}
          key={option.value}
        >
          <RadioGroupItem className="mt-0.5" value={option.value} />
          <span className="flex flex-col gap-0.5">
            <span className="font-medium">{option.label}</span>
            <span className="text-muted-foreground text-sm">{option.description}</span>
          </span>
        </label>
      ))}
    </RadioGroup>
  )
}

type AboutYouFormValues = { gender: string }

const AboutYouStep = ({
  formId,
  partner,
  isAnswered,
  onSaved,
  onSubmittingChange,
}: StepFormProps & { partner?: WelcomePartner; isAnswered: boolean }) => {
  'use no memo' // disable React Compiler to fix issues with react hook forms

  const { t } = useTranslation()
  const formMethods = useForm<AboutYouFormValues>({
    defaultValues: {
      gender: partner?.gender && (isAnswered || partner.gender !== 'PRIVATE') ? partner.gender : '',
    },
  })
  const [updatePartner] = useMutation(WELCOME_UPDATE_PARTNER)

  const onSubmit = async ({ gender }: AboutYouFormValues) => {
    if (!partner) {
      return
    }
    onSubmittingChange(true)
    try {
      await updatePartner({
        variables: {
          id: partner.id,
          input: { gender: gender as WelcomePartner['gender'], dateOfBirth: partner.dateOfBirth },
        },
      })
      onSaved()
    } catch (error) {
      toast.error((error as Error).message)
    } finally {
      onSubmittingChange(false)
    }
  }

  return (
    <Form<AboutYouFormValues> formMethods={formMethods} id={formId} onSubmit={onSubmit}>
      <EnumSelectFormField
        enumType={GenderEnum}
        helperText={t('Shown on your profile so members know who they are connecting with.')}
        label={t('Your gender')}
        name="gender"
        placeholder={t('Select your gender')}
        required
        validation={{ required: t('Please select your gender') }}
      />
    </Form>
  )
}

type PartnerFormValues = {
  nickName: string
  dateOfBirth: string | Date | null
  gender: string
}

const PartnerStep = ({
  formId,
  partner,
  onSaved,
  onSubmittingChange,
}: StepFormProps & { partner?: WelcomePartner }) => {
  'use no memo' // disable React Compiler to fix issues with react hook forms

  const { t } = useTranslation()
  const isPlaceholder = isBlankPartner(partner)
  const formMethods = useForm<PartnerFormValues>({
    defaultValues: {
      nickName: isPlaceholder ? '' : (partner?.nickName ?? ''),
      dateOfBirth: isPlaceholder ? null : toDateInputValue(partner?.dateOfBirth),
      gender: partner?.gender && partner.gender !== 'PRIVATE' ? partner.gender : '',
    },
  })
  const [createPartner] = useMutation(WELCOME_CREATE_PARTNER)
  const [updatePartner] = useMutation(WELCOME_UPDATE_PARTNER)

  const onSubmit = async ({ nickName, dateOfBirth, gender }: PartnerFormValues) => {
    if (!dateOfBirth) {
      return
    }
    onSubmittingChange(true)
    const input = {
      nickName: nickName.trim(),
      dateOfBirth: toDate(dateOfBirth),
      gender: gender as WelcomePartner['gender'],
    }
    try {
      if (partner) {
        await updatePartner({ variables: { id: partner.id, input } })
      } else {
        await createPartner({ variables: { input } })
      }
      onSaved()
    } catch (error) {
      toast.error((error as Error).message)
    } finally {
      onSubmittingChange(false)
    }
  }

  return (
    <Form<PartnerFormValues> formMethods={formMethods} id={formId} onSubmit={onSubmit}>
      <div className="flex flex-col gap-4">
        <InputFormField
          label={t("Partner's first name or nickname")}
          name="nickName"
          placeholder={t('e.g. Alex')}
          required
          validation={{
            required: t("Please enter your partner's name"),
            validate: (value: string) => value.trim().length > 0 || t("Please enter your partner's name"),
          }}
        />
        <DateFormField
          label={t("Partner's date of birth")}
          name="dateOfBirth"
          required
          validation={{
            validate: (value: string | Date | null) => validateDateOfBirth(value),
            required: { value: true, message: t('Invalid date provided') },
          }}
        />
        <EnumSelectFormField
          enumType={SELECTABLE_GENDERS}
          label={t("Partner's gender")}
          name="gender"
          placeholder={t('Select gender')}
          required
          validation={{ required: t("Please select your partner's gender") }}
        />
      </div>
    </Form>
  )
}

const PartnerIntro = ({ onAddPartner }: { onAddPartner: () => void }) => {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col items-center gap-6">
      <Button className="w-full sm:w-auto" onClick={onAddPartner} size="lg" type="button">
        <UserPlusIcon />
        {t('Add a partner to your profile')}
      </Button>
      <Alert>
        <LinkIcon aria-hidden className="size-4 shrink-0" />
        <AlertDescription>
          {t(
            'Does your partner have their own account? Once you are friends, you can link your profiles together from the partners page.'
          )}
        </AlertDescription>
      </Alert>
    </div>
  )
}

const LinkedPartnerNotice = () => {
  const { t } = useTranslation()

  return (
    <Alert>
      <InfoIcon aria-hidden className="size-4 shrink-0" />
      <AlertDescription>
        {t("Your partner is linked to their own profile. You can manage partners from your profile's partners page.")}
      </AlertDescription>
    </Alert>
  )
}

export const Route = createFileRoute('/(auth)/(fullscreen-layout)/auth/welcome')({
  staticData: {
    disableSeasonalTheme: true,
  },
  component: WelcomePage,
  head: () => {
    const t = i18n.t.bind(i18n)
    return seo({
      title: `${t('Welcome')} | ${env.VITE_APP_NAME}`,
      description: t('Welcome page'),
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    })
  },
})

const STEP_FORM_IDS = {
  [WelcomeStep.AboutYou]: 'welcome-about-you-form',
  [WelcomeStep.Partner]: 'welcome-partner-form',
} as const

function WelcomePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { currentUser, refreshSession, logOut } = useAuth()
  const profileId = currentUser?.profile?.id

  const { data, error, networkStatus, refetch } = useQuery(WELCOME_ONBOARDING_QUERY, {
    variables: { profileId },
    fetchPolicy: 'cache-and-network',
    skip: !profileId,
  })
  const profile = data?.profile

  const [step, setStep] = useState<WelcomeStep | null>(null)
  const [relationship, setRelationship] = useState<RelationshipEnum | null>(null)
  const [hasProfilePhoto, setHasProfilePhoto] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isAddingPartner, setIsAddingPartner] = useState(false)

  const [updateProfile] = useMutation(UPDATE_PROFILE_SIGN_UP_STEP)
  const [deletePartner] = useMutation(WELCOME_DELETE_PARTNER)

  const wizardTopRef = useRef<HTMLDivElement>(null)
  const previousStepRef = useRef<WelcomeStep | null>(null)

  useEffect(() => {
    if (step === null) {
      return
    }
    if (previousStepRef.current !== null && previousStepRef.current !== step) {
      wizardTopRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }
    previousStepRef.current = step
  }, [step])

  useEffect(() => {
    if (profile && step === null) {
      setStep(resumeStepFor(profile))
      setRelationship(profile.relationship === 'UNDEFINED' ? null : profile.relationship)
    }
  }, [profile, step])

  const handlePhotoSet = useCallback(() => setHasProfilePhoto(true), [])

  if (networkStatus === NetworkStatus.loading || (profile && step === null)) {
    return <SFPageLoading />
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-xl px-4 py-10">
        <ErrorAlert error={error} />
      </div>
    )
  }

  if (!(profile && profileId) || step === null) {
    return <SFPageLoading />
  }

  const [primaryPartner, secondPartner] = partnersOf(profile)
  const visibleSteps = visibleStepsFor(relationship)
  const stepPosition = Math.max(0, visibleSteps.indexOf(step))
  const isLastStep = stepPosition === visibleSteps.length - 1
  const progressValue = ((stepPosition + 1) / visibleSteps.length) * 100

  const saveStep = (nextStep: WelcomeStep) =>
    updateProfile({ variables: { id: profileId, input: { signUpStep: nextStep } } })

  const goToNextStep = async () => {
    const nextStep = visibleSteps[stepPosition + 1]
    if (nextStep === undefined) {
      return
    }
    await refetch()
    await saveStep(nextStep).catch(() => undefined)
    setStep(nextStep)
  }

  const hasSavedPartner = !!secondPartner && !isBlankPartner(secondPartner)
  const isLinkedPartnerStep = step === WelcomeStep.Partner && !!secondPartner?.profileId
  const isPartnerIntroStep =
    step === WelcomeStep.Partner && !isLinkedPartnerStep && !hasSavedPartner && !isAddingPartner

  const handleBack = () => {
    if (step === WelcomeStep.Partner && isAddingPartner && !hasSavedPartner) {
      setIsAddingPartner(false)
      return
    }
    const previousStep = visibleSteps[stepPosition - 1]
    if (previousStep !== undefined) {
      setStep(previousStep)
    }
  }

  const handleRelationshipContinue = async () => {
    if (!relationship) {
      return
    }
    setIsSubmitting(true)
    try {
      await updateProfile({
        variables: {
          id: profileId,
          input: { relationship, signUpStep: Math.max(profile.signUpStep, WelcomeStep.AboutYou) },
        },
      })
      if (!isCoupleRelationship(relationship) && secondPartner && isBlankPartner(secondPartner)) {
        await deletePartner({ variables: { id: secondPartner.id } })
      }
      await refetch()
      setStep(WelcomeStep.AboutYou)
    } catch (err) {
      toast.error(t('Failed to save your relationship status: {{message}}', { message: (err as Error).message }))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFinish = async () => {
    setIsSubmitting(true)
    try {
      await updateProfile({
        variables: { id: profileId, input: { signUpStep: WelcomeStep.Photo, signUpCompleted: true } },
      })
      await refreshSession()
      navigate({ to: '/feed' } as never)
    } catch (err) {
      toast.error(t('Failed to finish setup: {{message}}', { message: (err as Error).message }))
      setIsSubmitting(false)
    }
  }

  const handleContinueWithoutForm = () => {
    setIsSubmitting(true)
    goToNextStep().finally(() => setIsSubmitting(false))
  }

  const stepHeadings: Record<WelcomeStep, { title: string; description: string }> = {
    [WelcomeStep.Relationship]: {
      title: t("Let's set up your profile"),
      description: t('First, what best describes your relationship status? You can change this later.'),
    },
    [WelcomeStep.AboutYou]: {
      title: t('About you'),
      description: t('A few basics help members find the right connections.'),
    },
    [WelcomeStep.Partner]: isPartnerIntroStep
      ? {
          title: t('Include your partner?'),
          description: t('Your profile can represent both of you. You can also add multiple partners later.'),
        }
      : {
          title: t('Add your partner'),
          description: t('Add a few basics about your partner.'),
        },
    [WelcomeStep.Photo]: {
      title: t('Add a profile photo'),
      description: t('Profiles with a photo get far more connections. A photo is required to join the community.'),
    },
  }
  const heading = stepHeadings[step]

  const stepFormId =
    step === WelcomeStep.AboutYou || (step === WelcomeStep.Partner && !isLinkedPartnerStep && !isPartnerIntroStep)
      ? STEP_FORM_IDS[step]
      : undefined

  const renderPrimaryAction = () => {
    if (step === WelcomeStep.Relationship) {
      return (
        <Button disabled={!relationship || isSubmitting} onClick={handleRelationshipContinue} type="button">
          {isSubmitting ? <Spinner className="mr-2 size-4" /> : null}
          {t('Continue')}
        </Button>
      )
    }
    if (step === WelcomeStep.Photo) {
      return (
        <Button disabled={!hasProfilePhoto || isSubmitting} onClick={handleFinish} type="button">
          {isSubmitting ? <Spinner className="mr-2 size-4" /> : null}
          {t('Finish')}
        </Button>
      )
    }
    if (stepFormId) {
      return (
        <Button disabled={isSubmitting} form={stepFormId} type="submit">
          {isSubmitting ? <Spinner className="mr-2 size-4" /> : null}
          {t('Continue')}
        </Button>
      )
    }
    return (
      <Button
        disabled={isSubmitting}
        onClick={handleContinueWithoutForm}
        type="button"
        variant={isPartnerIntroStep ? 'outline' : 'default'}
      >
        {isSubmitting ? <Spinner className="mr-2 size-4" /> : null}
        {isPartnerIntroStep ? t('Skip for now') : t('Continue')}
      </Button>
    )
  }

  const handleFormSaved = () => {
    goToNextStep()
  }

  return (
    <div
      className="mx-auto flex min-h-screen w-full max-w-xl flex-col justify-center gap-6 px-4 py-8 md:py-6"
      ref={wizardTopRef}
    >
      <div className="flex items-center justify-center gap-2">
        <LogoSymbol imageClassName="w-8" />
        <span className="font-semibold text-xl">{t('Welcome to Spicy Friends')}</span>
      </div>

      <Card>
        <CardHeader className="gap-3">
          <div className="flex flex-col gap-2">
            <span className="text-muted-foreground text-sm">
              {t('Step {{current}} of {{total}}', { current: stepPosition + 1, total: visibleSteps.length })}
            </span>
            <Progress aria-label={t('Setup progress')} value={progressValue} />
          </div>
          <CardTitle className="text-xl md:text-2xl">{heading.title}</CardTitle>
          <CardDescription>{heading.description}</CardDescription>
        </CardHeader>

        <CardContent>
          {step === WelcomeStep.Relationship ? (
            <RelationshipStep onValueChange={setRelationship} value={relationship} />
          ) : null}

          {step === WelcomeStep.AboutYou ? (
            <AboutYouStep
              formId={STEP_FORM_IDS[WelcomeStep.AboutYou]}
              isAnswered={hasCompletedAboutYou(profile)}
              onSaved={handleFormSaved}
              onSubmittingChange={setIsSubmitting}
              partner={primaryPartner}
            />
          ) : null}

          {step === WelcomeStep.Partner && isLinkedPartnerStep ? <LinkedPartnerNotice /> : null}

          {isPartnerIntroStep ? <PartnerIntro onAddPartner={() => setIsAddingPartner(true)} /> : null}

          {step === WelcomeStep.Partner && !(isLinkedPartnerStep || isPartnerIntroStep) ? (
            <PartnerStep
              formId={STEP_FORM_IDS[WelcomeStep.Partner]}
              onSaved={handleFormSaved}
              onSubmittingChange={setIsSubmitting}
              partner={secondPartner}
            />
          ) : null}

          {step === WelcomeStep.Photo ? <WelcomePageProfile onPhotoSet={handlePhotoSet} profileId={profileId} /> : null}
        </CardContent>

        <CardFooter className="flex justify-between gap-3">
          <Button disabled={stepPosition === 0 || isSubmitting} onClick={handleBack} type="button" variant="ghost">
            {t('Back')}
          </Button>
          {renderPrimaryAction()}
        </CardFooter>
      </Card>

      {isLastStep ? null : (
        <p className="text-center text-muted-foreground text-sm">
          {t('You can fill in the rest of your profile any time after setup.')}
        </p>
      )}

      <Button
        className="self-center text-muted-foreground"
        disabled={logOut.isPending}
        onClick={() => logOut.mutate()}
        size="sm"
        type="button"
        variant="link"
      >
        {t('Sign out')}
      </Button>
    </div>
  )
}
